import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from "@aient/types/database"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type Entitlements = {
  maxSessionsPerMonth: number | null
  maxRepos: number | null
  maxSlackWorkspaces: number | null
  aiPrsEnabled: boolean
}

export const DEFAULT_ENTITLEMENTS: Entitlements = {
  maxSessionsPerMonth: 10000,
  maxRepos: 1,
  maxSlackWorkspaces: 1,
  aiPrsEnabled: false,
}

/**
 * Valid subscription statuses that allow access to paid features
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing'] as const
export type ActiveSubscriptionStatus = typeof ACTIVE_SUBSCRIPTION_STATUSES[number]

/**
 * All possible Stripe subscription statuses
 */
export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'paused'
  | null

// Map Stripe price ids to entitlements. Use env to inject live ids.
import { NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID, NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID } from "@/lib/env"

/**
 * Derive entitlements from a Stripe price ID.
 * Also applies plan_overrides from the organisation for enterprise customers.
 */
export function deriveEntitlements(
  stripePriceId?: string | null,
  planOverrides?: Record<string, unknown> | null,
  isComplimentary = false,
): Entitlements {
  if (isComplimentary) {
    return { maxSessionsPerMonth: null, maxRepos: null, maxSlackWorkspaces: null, aiPrsEnabled: true }
  }
  let baseEntitlements: Entitlements

  if (!stripePriceId) {
    baseEntitlements = DEFAULT_ENTITLEMENTS
  } else if (stripePriceId === NEXT_PUBLIC_STRIPE_PRICE_STARTUP_ID) {
    baseEntitlements = {
      maxSessionsPerMonth: 100000,
      maxRepos: 5,
      maxSlackWorkspaces: 1,
      aiPrsEnabled: false,
    }
  } else if (stripePriceId === NEXT_PUBLIC_STRIPE_PRICE_GROWTH_ID) {
    baseEntitlements = {
      maxSessionsPerMonth: 1000000,
      maxRepos: null,
      maxSlackWorkspaces: null,
      aiPrsEnabled: true,
    }
  } else {
    // Unknown/custom price - use defaults but may be overridden
    baseEntitlements = DEFAULT_ENTITLEMENTS
  }

  // Apply plan_overrides for enterprise customers
  if (planOverrides && typeof planOverrides === 'object') {
    return {
      maxSessionsPerMonth: typeof planOverrides.maxSessionsPerMonth === 'number'
        ? planOverrides.maxSessionsPerMonth
        : planOverrides.maxSessionsPerMonth === null
          ? null
          : baseEntitlements.maxSessionsPerMonth,
      maxRepos: typeof planOverrides.maxRepos === 'number'
        ? planOverrides.maxRepos
        : planOverrides.maxRepos === null
          ? null
          : baseEntitlements.maxRepos,
      maxSlackWorkspaces: typeof planOverrides.maxSlackWorkspaces === 'number'
        ? planOverrides.maxSlackWorkspaces
        : planOverrides.maxSlackWorkspaces === null
          ? null
          : baseEntitlements.maxSlackWorkspaces,
      aiPrsEnabled: typeof planOverrides.aiPrsEnabled === 'boolean'
        ? planOverrides.aiPrsEnabled
        : baseEntitlements.aiPrsEnabled,
    }
  }

  return baseEntitlements
}

/**
 * Check if a subscription status allows access to paid features.
 * Only 'active' and 'trialing' statuses allow access.
 */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  if (!status) return false
  return ACTIVE_SUBSCRIPTION_STATUSES.includes(status as ActiveSubscriptionStatus)
}

/**
 * Check if an organisation has an active subscription.
 * Returns true if subscription_status is 'active' or 'trialing'.
 */
export async function hasActiveSubscription(
  supabase: SupabaseClient<Database>,
  organisationId: string
): Promise<boolean> {
  const { data: org } = await supabase
    .from('organisations')
    .select('subscription_status')
    .eq('id', organisationId)
    .maybeSingle()
  return isSubscriptionActive(org?.subscription_status as SubscriptionStatus)
}

/**
 * Check if an organisation has capacity to add more repositories.
 */
export async function hasRepoCapacity(supabase: SupabaseClient<Database>, organisationId: string): Promise<boolean> {
  const { data: org } = await supabase
    .from('organisations')
    .select('stripe_price_id, plan_overrides')
    .eq('id', organisationId)
    .maybeSingle()
  const ent = deriveEntitlements(org?.stripe_price_id, org?.plan_overrides as Record<string, unknown> | null)
  if (ent.maxRepos == null) return true
  const { count } = await supabase
    .from('repositories')
    .select('id', { count: 'exact', head: true })
    .eq('organisation_id', organisationId)
  return (count ?? 0) < ent.maxRepos
}

/**
 * Check if an organisation has capacity to add more Slack workspaces.
 */
export async function hasSlackWorkspaceCapacity(
  supabase: SupabaseClient<Database>,
  organisationId: string
): Promise<boolean> {
  const { data: org } = await supabase
    .from('organisations')
    .select('stripe_price_id, plan_overrides')
    .eq('id', organisationId)
    .maybeSingle()
  const ent = deriveEntitlements(org?.stripe_price_id, org?.plan_overrides as Record<string, unknown> | null)
  if (ent.maxSlackWorkspaces == null) return true
  const { count } = await supabase
    .from('slack_installations')
    .select('id', { count: 'exact', head: true })
    .eq('organisation_id', organisationId)
  return (count ?? 0) < ent.maxSlackWorkspaces
}

/**
 * Check if an organisation can create AI-generated PRs.
 * Requires both an active subscription and the aiPrsEnabled entitlement.
 */
export async function canCreateAiPr(
  supabase: SupabaseClient<Database>,
  organisationId: string
): Promise<{ allowed: boolean; reason?: string }> {
  const { data: org } = await supabase
    .from('organisations')
    .select('stripe_price_id, subscription_status, plan_overrides')
    .eq('id', organisationId)
    .maybeSingle()

  if (!org) {
    return { allowed: false, reason: 'organisation_not_found' }
  }

  // Check subscription status
  if (!isSubscriptionActive(org.subscription_status as SubscriptionStatus)) {
    return { allowed: false, reason: 'subscription_inactive' }
  }

  const ent = deriveEntitlements(org.stripe_price_id, org.plan_overrides as Record<string, unknown> | null)

  if (!ent.aiPrsEnabled) {
    return { allowed: false, reason: 'feature_not_included' }
  }

  return { allowed: true }
}

/**
 * Get the full entitlements for an organisation, including subscription validation.
 * Returns null if the organisation doesn't have an active subscription.
 */
export async function getOrganisationEntitlements(
  supabase: SupabaseClient<Database>,
  organisationId: string
): Promise<{ entitlements: Entitlements; isActive: boolean; status: SubscriptionStatus }> {
  const { data: org } = await supabase
    .from('organisations')
    .select('stripe_price_id, subscription_status, plan_overrides')
    .eq('id', organisationId)
    .maybeSingle()

  const status = (org?.subscription_status as SubscriptionStatus) ?? null
  const isActive = isSubscriptionActive(status)
  const entitlements = deriveEntitlements(
    org?.stripe_price_id,
    org?.plan_overrides as Record<string, unknown> | null
  )

  return { entitlements, isActive, status }
}
